import { Controller } from "@hotwired/stimulus"

// Combobox controller for autocomplete/typeahead functionality
// Supports local filtering and remote loading
export default class extends Controller {
  static targets = ["input", "hidden", "list", "option", "empty", "loading", "clear"]
  static values = {
    remoteUrl: String,
    minChars: { type: Number, default: 1 },
    debounce: { type: Number, default: 300 }
  }

  connect() {
    this.isOpen = false
    this.highlightedIndex = -1
    this.allOptions = this.optionTargets.map(el => ({
      element: el,
      value: el.dataset.value,
      label: el.dataset.label?.toLowerCase() || "",
      description: el.dataset.description?.toLowerCase() || ""
    }))

    // Close when clicking outside
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)

    // Debounce timer for remote fetching
    this.debounceTimer = null

    // Show clear button if there's an initial value
    this.updateClearButton()
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  // Open dropdown on focus
  focus() {
    this.open()
  }

  // Handle input typing
  input() {
    const query = this.inputTarget.value.toLowerCase().trim()

    // Always filter local options immediately
    this.filterLocal(query)

    // Open the dropdown
    this.open()

    // Trigger remote fetch if configured and query meets minimum chars
    if (this.hasRemoteUrlValue && query.length >= this.minCharsValue) {
      this.debouncedFetch(query)
    }
  }

  // Filter local options by query
  filterLocal(query) {
    let visibleCount = 0

    this.allOptions.forEach(opt => {
      const matches = query === "" ||
        opt.label.includes(query) ||
        opt.description.includes(query)

      opt.element.classList.toggle("hidden", !matches)
      if (matches) visibleCount++
    })

    // Show/hide empty state
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visibleCount > 0 || query === "")
    }

    // Reset highlight when filtering
    this.highlightedIndex = -1
    this.updateHighlight()
  }

  // Debounced remote fetch
  debouncedFetch(query) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchRemote(query)
    }, this.debounceValue)
  }

  // Fetch options from remote URL
  async fetchRemote(query) {
    if (!this.hasRemoteUrlValue) return

    // Show loading state
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }

    try {
      const url = new URL(this.remoteUrlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Network response was not ok")

      const data = await response.json()
      this.mergeRemoteOptions(data.options || [])
    } catch (error) {
      console.error("Combobox fetch error:", error)
    } finally {
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add("hidden")
      }
    }
  }

  // Merge remote options with existing ones (dedupe by value)
  mergeRemoteOptions(remoteOptions) {
    const existingValues = new Set(this.allOptions.map(o => o.value))

    remoteOptions.forEach(opt => {
      if (existingValues.has(String(opt.value))) return

      const li = this.createOptionElement(opt)
      this.listTarget.appendChild(li)

      this.allOptions.push({
        element: li,
        value: String(opt.value),
        label: opt.label?.toLowerCase() || "",
        description: opt.description?.toLowerCase() || ""
      })
    })

    // Re-filter to apply current query to new options
    const query = this.inputTarget.value.toLowerCase().trim()
    this.filterLocal(query)
  }

  // Create a new option element for remote data
  createOptionElement(opt) {
    const li = document.createElement("li")
    li.setAttribute("role", "option")
    li.setAttribute("data-ui--combobox-target", "option")
    li.setAttribute("data-value", opt.value)
    li.setAttribute("data-label", opt.label)
    li.setAttribute("data-description", opt.description || "")
    li.setAttribute("data-action", "click->ui--combobox#select mouseenter->ui--combobox#highlight")
    li.className = "relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 hover:bg-indigo-600 hover:text-white"

    const wrapper = document.createElement("div")
    wrapper.className = "flex"

    const labelSpan = document.createElement("span")
    labelSpan.className = "truncate"
    labelSpan.textContent = opt.label

    wrapper.appendChild(labelSpan)

    if (opt.description) {
      const descSpan = document.createElement("span")
      descSpan.className = "ml-2 truncate text-gray-500 group-hover:text-white"
      descSpan.textContent = opt.description
      wrapper.appendChild(descSpan)
    }

    li.appendChild(wrapper)
    return li
  }

  // Keyboard navigation
  keydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (!this.isOpen) {
          this.open()
        } else {
          this.highlightNext()
        }
        break
      case "ArrowUp":
        event.preventDefault()
        if (this.isOpen) {
          this.highlightPrevious()
        }
        break
      case "Enter":
        event.preventDefault()
        if (this.isOpen && this.highlightedIndex >= 0) {
          this.selectHighlighted()
        }
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
      case "Tab":
        this.close()
        break
    }
  }

  // Open the dropdown
  open() {
    if (this.isOpen) return
    this.isOpen = true
    this.listTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  // Close the dropdown
  close() {
    if (!this.isOpen) return
    this.isOpen = false
    this.listTarget.classList.add("hidden")
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.highlightedIndex = -1
    this.updateHighlight()
  }

  // Toggle dropdown
  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
      this.inputTarget.focus()
    }
  }

  // Click outside handler
  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  // Get visible options
  get visibleOptions() {
    return this.optionTargets.filter(el => !el.classList.contains("hidden"))
  }

  // Highlight next option
  highlightNext() {
    const visible = this.visibleOptions
    if (visible.length === 0) return

    this.highlightedIndex = Math.min(this.highlightedIndex + 1, visible.length - 1)
    this.updateHighlight()
    this.scrollToHighlighted()
  }

  // Highlight previous option
  highlightPrevious() {
    const visible = this.visibleOptions
    if (visible.length === 0) return

    this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
    this.updateHighlight()
    this.scrollToHighlighted()
  }

  // Update highlight visual state
  updateHighlight() {
    const visible = this.visibleOptions

    visible.forEach((el, index) => {
      if (index === this.highlightedIndex) {
        el.setAttribute("aria-selected", "true")
        el.classList.add("bg-indigo-600", "text-white")
        el.classList.remove("text-gray-900")
        // Update description color within highlighted item
        const desc = el.querySelector(".text-gray-500")
        if (desc) desc.classList.add("text-indigo-200")
      } else {
        el.setAttribute("aria-selected", "false")
        el.classList.remove("bg-indigo-600", "text-white")
        el.classList.add("text-gray-900")
        // Reset description color
        const desc = el.querySelector(".text-indigo-200")
        if (desc) {
          desc.classList.remove("text-indigo-200")
          desc.classList.add("text-gray-500")
        }
      }
    })
  }

  // Scroll to keep highlighted option visible
  scrollToHighlighted() {
    const visible = this.visibleOptions
    if (this.highlightedIndex >= 0 && visible[this.highlightedIndex]) {
      visible[this.highlightedIndex].scrollIntoView({ block: "nearest" })
    }
  }

  // Highlight option on mouse enter
  highlight(event) {
    const visible = this.visibleOptions
    const index = visible.indexOf(event.currentTarget)
    if (index >= 0) {
      this.highlightedIndex = index
      this.updateHighlight()
    }
  }

  // Select highlighted option
  selectHighlighted() {
    const visible = this.visibleOptions
    if (this.highlightedIndex >= 0 && visible[this.highlightedIndex]) {
      this.selectOption(visible[this.highlightedIndex])
    }
  }

  // Select option on click
  select(event) {
    this.selectOption(event.currentTarget)
  }

  // Common select logic
  selectOption(optionElement) {
    const value = optionElement.dataset.value
    const label = optionElement.dataset.label
    const previousValue = this.hiddenTarget.value

    // Update hidden input with selected value
    this.hiddenTarget.value = value

    // Update visible input with selected label
    this.inputTarget.value = label

    // Dispatch native change event on hidden input (for form compatibility)
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))

    // Dispatch Stimulus events for app controllers to listen to
    this.dispatch("select", { detail: { value, label, previousValue } })
    this.dispatch("change", { detail: { value, label, previousValue } })

    this.updateClearButton()
    this.close()
  }

  // Clear the selection
  clear(event) {
    event.preventDefault()
    event.stopPropagation()

    const previousValue = this.hiddenTarget.value

    // Clear both inputs
    this.hiddenTarget.value = ""
    this.inputTarget.value = ""

    // Dispatch native change event (for form compatibility)
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))

    // Dispatch Stimulus events for app controllers
    this.dispatch("clear", { detail: { previousValue } })
    this.dispatch("change", { detail: { value: "", label: "", previousValue } })

    // Reset filtering to show all options
    this.filterLocal("")

    this.updateClearButton()
    this.inputTarget.focus()
  }

  // Show/hide clear button based on selection
  updateClearButton() {
    if (this.hasClearTarget) {
      const hasValue = this.hiddenTarget.value !== ""
      this.clearTarget.classList.toggle("hidden", !hasValue)
      this.clearTarget.classList.toggle("flex", hasValue)
    }
  }
}
